<template>
  <SideBar />

  <main id="sidenavmain" class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
    <NavBar />

    <div class="py-4 container-fluid mb-4">
      <div class="col-lg-12 position-relative mt-n3">

        <div class="ts-head d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3 mb-3">
          <div class="ts-head-left d-flex align-items-center gap-2">
            <h6 class="ts-title mb-0">Grades</h6>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-if="!isLoading">{{ totalItems }}</span>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-else>...</span>
          </div>
          <div class="ts-head-right d-flex flex-wrap gap-2 justify-content-start justify-content-xl-end align-items-center">
            <div class="ts-search-wrap flex-grow-1 flex-md-grow-0" style="min-width: 220px;">
              <svg class="ts-search-ico" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input v-model="searchQuery" @input="debounceSearch" class="ts-search-input w-100" placeholder="Search grades..." />
            </div>

            <!-- Curriculum filter -->
            <select v-model.number="curriculumFilter" class="ts-search-input" style="min-width: 180px;">
              <option :value="0">All curricula</option>
              <option v-for="c in curricula" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>

            <button class="ts-btn-add d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0" @click="openAddModal">
              Add New
              <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="ms-1">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="card ts-card">
          <div class="ts-card-body">
            <div v-if="!isLoading && paginatedItems.length === 0" class="ts-empty">
              <p class="ts-empty-title">No grades found</p>
              <p class="ts-empty-sub">Add your grade boundaries for this school's curriculum</p>
            </div>

            <div v-show="isLoading || paginatedItems.length > 0" class="table-responsive d-none d-md-block">
              <table class="ts-table" data-ts-mobile="cards">
                <thead>
                <tr>
                  <th>Grade</th>
                  <th>Short form</th>
                  <th class="text-center">Points</th>
                  <th>Curriculum</th>
                  <th class="text-center" style="width:130px">Actions</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="isLoading">
                  <tr v-for="n in pageSize" :key="'sk'+n" class="ts-skel-row">
                    <td data-label="Grade"><div class="ts-skel" style="width:50px;height:12px"></div></td>
                    <td data-label="Short form"><div class="ts-skel" style="width:40px;height:12px"></div></td>
                    <td data-label="Points" class="text-center"><div class="ts-skel" style="width:40px;height:12px;margin:0 auto"></div></td>
                    <td data-label="Curriculum"><div class="ts-skel" style="width:100px;height:12px"></div></td>
                    <td data-label="Actions"><div class="ts-skel" style="width:90px;height:28px;border-radius:8px;margin:0 auto"></div></td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="item in paginatedItems" :key="item.id">
                    <td data-label="Grade"><strong>{{ item.grade }}</strong></td>
                    <td data-label="Short form"><span class="text-muted">{{ item.gshotfrm || '—' }}</span></td>
                    <td data-label="Points" class="text-center">{{ formatPoints(item.points) }}</td>
                    <td data-label="Curriculum">{{ curriculumLabel(item.gradecategory) }}</td>
                    <td data-label="Actions">
                      <div class="ts-actions justify-content-center">
                        <button class="ts-btn-edit" @click="editItem(item)" title="Edit">
                          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                        </button>
                        <button class="ts-btn-suspend" @click="deleteItem(item)" title="Delete">
                          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>

            <div class="d-md-none ts-mobile-list" v-show="isLoading || paginatedItems.length > 0">
              <template v-if="!isLoading">
                <div v-for="item in paginatedItems" :key="'m'+item.id" class="ts-card-m">
                  <div class="ts-card-top">
                    <div class="ts-card-info">
                      <div class="ts-name">{{ item.grade }}</div>
                      <div class="ts-email">{{ curriculumLabel(item.gradecategory) }}</div>
                    </div>
                    <span class="text-xs font-weight-bold">{{ formatPoints(item.points) }} pts</span>
                  </div>
                  <div class="ts-card-meta">
                    <div class="ts-meta-row">
                      <span class="ts-ml">Short form</span>
                      <span class="ts-mv">{{ item.gshotfrm || '—' }}</span>
                    </div>
                  </div>
                  <div class="ts-card-acts">
                    <button class="ts-btn-edit-full" @click="editItem(item)">Edit</button>
                    <button class="ts-btn-suspend-full" @click="deleteItem(item)">Delete</button>
                  </div>
                </div>
              </template>
            </div>

            <div v-if="!isLoading && totalItems > 0" class="ts-pagination">
              <span class="ts-pg-info">Showing {{ paginationFrom }} to {{ paginationTo }} of {{ totalItems }}</span>
              <div class="ts-pg-btns">
                <button class="ts-pg-btn" @click="currentPage--" :disabled="currentPage === 1">‹</button>
                <button v-for="p in pageButtons" :key="p" class="ts-pg-btn" :class="{ 'ts-pg-active': p === currentPage }" @click="currentPage = p">{{ p }}</button>
                <button class="ts-pg-btn" @click="currentPage++" :disabled="currentPage === lastPage">›</button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    <AppFooter />
  </main>

  <!-- ═══ ADD/EDIT MODAL ═══ -->
  <div v-if="showModal" class="tw-overlay z-index-10000" @mousedown.self="closeModal">
    <div class="tw-modal" style="max-width: 460px;">
      <div class="tw-header">
        <h5 class="tw-title">{{ isEditing ? 'Edit grade' : 'Add grade' }}</h5>
        <button class="tw-close" @click="closeModal">&times;</button>
      </div>
      <div class="tw-body" style="background: var(--surface-2, #f8fafc);">
        <div class="card p-3 shadow-sm border-0">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="tw-label">Grade <span class="tw-req">*</span></label>
              <input v-model="form.grade" type="text" class="tw-input" maxlength="50" placeholder="e.g. A" />
            </div>
            <div class="col-md-6">
              <label class="tw-label">Short form</label>
              <input v-model="form.gshotfrm" type="text" class="tw-input" maxlength="50" placeholder="e.g. A" />
              <p class="text-xs text-muted mb-0 mt-1">Compact version for reports.</p>
            </div>

            <div class="col-md-6">
              <label class="tw-label">Points <span class="tw-req">*</span></label>
              <input v-model.number="form.points" type="number" min="0" max="100" step="0.01" class="tw-input" />
              <p class="text-xs text-muted mb-0 mt-1">Used for mean calculations.</p>
            </div>
            <div class="col-md-6">
              <label class="tw-label">Curriculum <span class="tw-req">*</span></label>
              <select v-model.number="form.gradecategory" class="tw-input">
                <option v-for="c in curricula" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="tw-footer d-flex justify-content-end gap-2 p-3 bg-white border-top">
        <button class="tw-btn tw-btn-ghost" @click="closeModal">Cancel</button>
        <button class="tw-btn tw-btn-primary" @click="submitForm" :disabled="isSubmitting">
          {{ isSubmitting ? 'Saving...' : (isEditing ? 'Save' : 'Add') }}
        </button>
      </div>
    </div>
  </div>

</template>

<script>
/* eslint-disable */
import SideBar from "@/fragments/exam/AcademicsSidebar.vue";
import NavBar from "@/fragments/exam/AcademicsNavBar.vue";
import AppFooter from "@/fragments/Footer.vue";
import apiService from '@/apis/apiService';
import swal from 'sweetalert';

// Curriculum options match the backend's GradeDto validation and the
// classes page's category selector. Keep in sync across both.
const CURRICULA = [
  { value: 1, label: '8-4-4' },
  { value: 2, label: 'CBC' },
  { value: 3, label: 'IGCSE / Cambridge' },
  { value: 4, label: 'IB' },
  { value: 5, label: 'American / AP' },
];

export default {
  name: "GradesPage",
  components: { SideBar, NavBar, AppFooter },

  data() {
    return {
      items: [],
      isLoading: true,
      searchQuery: '',
      curriculumFilter: 0,
      currentPage: 1,
      pageSize: 10,
      searchTimer: null,

      curricula: CURRICULA,

      showModal: false,
      isEditing: false,
      isSubmitting: false,
      form: this.blankForm(),
    };
  },

  computed: {
    filteredItems() {
      let arr = this.items;
      if (this.curriculumFilter) {
        arr = arr.filter(i => i.gradecategory === this.curriculumFilter);
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        arr = arr.filter(i =>
            (i.grade || '').toLowerCase().includes(q) ||
            (i.gshotfrm || '').toLowerCase().includes(q)
        );
      }
      return arr;
    },
    totalItems() { return this.filteredItems.length; },
    lastPage() { return Math.ceil(this.totalItems / this.pageSize) || 1; },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    paginationFrom() { return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    paginationTo() { return Math.min(this.currentPage * this.pageSize, this.totalItems); },
    pageButtons() { return Array.from({ length: this.lastPage }, (_, i) => i + 1); },
  },

  mounted() {
    this.fetchItems();
    if (window.innerWidth >= 1024) {
      import('perfect-scrollbar').then((m) => {
        const PS = m.default;
        const c = document.getElementById('sidenavmain');
        if (c) new PS(c);
      });
    }
  },

  methods: {
    curriculumLabel(value) {
      if (value == null) return '—';
      const c = CURRICULA.find(x => x.value === Number(value));
      return c ? c.label : '—';
    },

    formatPoints(n) {
      if (n == null) return '—';
      // Display with up to 2 decimal places, trimming trailing zeroes.
      return Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    async fetchItems() {
      this.isLoading = true;
      try {
        const res = await apiService.apiClient.get('/api/academics/grades');
        this.items = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.items = [];
        swal("Error", this.readErr(e, "Could not load grades"), "error");
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.currentPage = 1; }, 300);
    },

    openAddModal() {
      this.isEditing = false;
      // Pre-select the currently-filtered curriculum for convenience —
      // if the user's filtering by CBC, chances are they want to add
      // another CBC grade.
      this.form = this.blankForm();
      if (this.curriculumFilter) this.form.gradecategory = this.curriculumFilter;
      this.showModal = true;
    },

    editItem(item) {
      this.form = {
        id: item.id,
        grade: item.grade,
        gshotfrm: item.gshotfrm,
        points: item.points,
        gradecategory: item.gradecategory,
      };
      this.isEditing = true;
      this.showModal = true;
    },

    closeModal() { this.showModal = false; },

    async submitForm() {
      if (!this.form.grade || !this.form.grade.trim()) {
        swal("Required", "Grade letter is required", "warning");
        return;
      }
      if (this.form.points == null || isNaN(this.form.points)) {
        swal("Required", "Points value is required", "warning");
        return;
      }
      if (!this.form.gradecategory) {
        swal("Required", "Select a curriculum", "warning");
        return;
      }

      this.isSubmitting = true;
      try {
        if (this.isEditing) {
          await apiService.apiClient.put(`/api/academics/grades/${this.form.id}`, this.form);
        } else {
          await apiService.apiClient.post('/api/academics/grades', this.form);
        }
        swal("Saved", "Grade saved", "success");
        this.closeModal();
        this.fetchItems();
      } catch (e) {
        swal("Error", this.readErr(e, "Could not save"), "error");
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteItem(item) {
      const ok = await swal({
        title: "Delete this grade?",
        text: `"${item.grade}" (${this.curriculumLabel(item.gradecategory)}) will be removed.`,
        icon: "warning",
        buttons: ["Cancel", "Delete"],
        dangerMode: true,
      });
      if (!ok) return;
      try {
        await apiService.apiClient.delete(`/api/academics/grades/${item.id}`);
        swal("Deleted", "Grade removed", "success");
        this.fetchItems();
      } catch (e) {
        swal("Cannot delete", this.readErr(e, "Delete failed"), "error");
      }
    },

    blankForm() {
      return {
        id: null,
        grade: '',
        gshotfrm: '',
        points: 0,
        gradecategory: 1,
      };
    },

    readErr(e, fb) { return (e?.response?.data?.querystatus) || fb; },
  },
};
</script>

<style scoped src="@/assets/css/ts-table.css"></style>